import {
	AccessoryMountingSide,
	ProductTypeVariation,
	ProductTypeVariationAccessory,
} from '@/api/ProductTypeVariationApi';
import { Product } from '@/api/ProductApi';
import { CreatorAction, UUID } from './types';
import { AttributeValue } from '@/api/AttributeValueApi';
import { Attribute, AttributeTarget } from '@/api/AttributeApi';
import { SerializedRenderer, UserDataObjectType } from '@/renderer/types';
import { ProductType } from '@/api/ProductTypeApi';
import { Accessory, AccessoryType } from '@/api/AccessoryApi';
import { Door } from '@/api/DoorApi';

export enum CreatorStep {
	DIMENSIONS,
	ACCESSORIES,
	COLORS,
	SUMMARY,
}

export enum PostType {
	UPPER,
	LOWER,
	MIDDLE,
}

export enum NotificationID {
	MISSING_ANSWERS = 'missing-answers',
	COLLISION_DETECTED = 'collision-detected',
	MIDDLE_POSTS_LIMIT_REACHED = 'middle-posts-limit-reached',
	MIDDLE_POSTS_MAX_SPACING_EXCEEDED = 'middle-posts-max-spacing-exceeded',
}

export interface CreatorPost {
	id: UUID;
	posY: number;
	type: PostType;
	linkedCreatorAccessoryId: UUID | null;
	selectedValues: Record<UUID, AttributeValue | null>;
	customValues: Record<UUID, string>;
	isAdjustable: boolean;
	isAutoAdded: boolean;
}

export interface SerializedCreatorPost {
	id: UUID;
	posY: number;
	type: PostType;
	linkedCreatorAccessoryId: UUID | null;
	selectedValues: Record<UUID, UUID | null>;
	customValues: Record<UUID, string>;
	isAutoAdded: boolean;
	isAdjustable: boolean;
}

export interface CreatorEnding {
	selectedValues: Record<UUID, AttributeValue | null>;
	customValues: Record<UUID, string>;
}

export interface SerializedCreatorEnding {
	selectedValues: Record<UUID, UUID | null>;
	customValues: Record<UUID, string>;
}

export type ActionsOnAccessoryDelete = Record<
	UUID,
	Record<UUID, CreatorAction[]>
>;

export interface CreatorOptions {
	collisionDetected: boolean;
	isMobileAsideOpened: boolean;
	currentStep: CreatorStep;
	withExtraPosts: boolean;
	autoMiddlePostPositioning: boolean;
	accessoriesDrawerType: '' | AccessoryType;
	endingsConfiguration: {
		upperEnding: boolean;
		lowerEnding: boolean;
	};
	actionsOnAccessoryDelete: ActionsOnAccessoryDelete;
	accessoriesAccordionOpened: UUID | null;
	pendingRendererActions: CreatorAction[];
	availableSteps: CreatorStep[];
	enabledSteps: CreatorStep[];
	measurementVisible: boolean;
}

export interface CreatorRendererOptions {
	loading: boolean;
	distance: number;
}

export type AttributeWithValues = Attribute & {
	attributeValues: AttributeValue[];
};

export type NonAccessoryAttributeTarget = Exclude<
	AttributeTarget,
	AttributeTarget.ACCESSORIES
>;

export type AvailableAttributeValues = Record<
	NonAccessoryAttributeTarget,
	AttributeWithValues[]
>;

export type AvailableAccessories = Record<
	AccessoryType,
	ProductTypeVariationAccessory[]
>;

export interface CreatorAccessoryOptions {
	autoPositioning: boolean;
}

export interface CreatorAccessory {
	id?: UUID;
	refId: UUID;
	posY: number;
	parentCreatorAccessoryRefId: UUID | null;
	linkedMiddlePostId: UUID | null;
	variationAccessory: ProductTypeVariationAccessory;
	customFieldAnswers: Record<string, string>;
	customValues: Record<UUID, string>;
	selectedValues: Record<UUID, AttributeValue | null>;
	mountingSide: Exclude<AccessoryMountingSide, AccessoryMountingSide.BOTH>;
	availableAttributes: AttributeWithValues[];
	options: CreatorAccessoryOptions;
}

export interface SerializedCreatorAccessory {
	id?: UUID;
	refId: UUID;
	posY: number;
	variationAccessoryId: UUID;
	parentCreatorAccessoryRefId: CreatorAccessory['parentCreatorAccessoryRefId'];
	linkedMiddlePostId: UUID | null;
	customFieldAnswers: Record<string, string>;
	customValues: Record<UUID, string>;
	selectedValues: Record<UUID, UUID | null>;
	mountingSide: Exclude<AccessoryMountingSide, AccessoryMountingSide.BOTH>;
	options: CreatorAccessoryOptions;
	ledTapesData: { posY: number; length: number }[];
}

export type AdjustableHeights = {
	handle: number;
	upperEnding: number;
	lowerEnding: number;
	total: number;
};

export type CreatorEndings = {
	[UserDataObjectType.UPPER_ENDING_ADJUSTABLE]: CreatorEnding;
	[UserDataObjectType.LOWER_ENDING_ADJUSTABLE]: CreatorEnding;
};

export type SerializedCreatorEndings = {
	[UserDataObjectType.UPPER_ENDING_ADJUSTABLE]: SerializedCreatorEnding;
	[UserDataObjectType.LOWER_ENDING_ADJUSTABLE]: SerializedCreatorEnding;
};

export interface CreatorParams {
	productTypeId: UUID;
	creatorPosts: CreatorPost[];
	adjustableHeights: AdjustableHeights;
	selectedValues: Record<UUID, AttributeValue | null>;
	customValues: Record<UUID, string>;
	creatorAccessories: Omit<CreatorAccessory, 'availableAttributes'>[];
	creatorEndings: CreatorEndings;
	exportedRenderer: SerializedRenderer;
	options: CreatorOptions;
}

export interface SerializedCreator {
	productTypeId: UUID;
	creatorPosts: SerializedCreatorPost[];
	adjustableHeights: AdjustableHeights;
	selectedValues: Record<UUID, UUID | null>;
	customValues: Record<UUID, string>;
	creatorAccessories: SerializedCreatorAccessory[];
	creatorEndings: SerializedCreatorEndings;
	exportedRenderer: SerializedRenderer;
	options: CreatorOptions;
}

export interface CreatorState {
	configurationName: string;
	product: Product | null;
	productType: ProductType | null;
	allAttributes: Attribute[];
	allAccessories: Accessory[];
	productTypeVariations: ProductTypeVariation[];
	productTypeVariation: ProductTypeVariation | null;
	availableAttributeValues: AvailableAttributeValues;
	selectedValues: Record<UUID, AttributeValue | null>;
	customValues: Record<UUID, string>;
	adjustableHeights: AdjustableHeights;
	creatorPosts: CreatorPost[];
	availableAccessories: AvailableAccessories;
	creatorAccessories: CreatorAccessory[];
	creatorEndings: CreatorEndings;
	selectedDoor: Door | null;
	renderer: CreatorRendererOptions;
	options: CreatorOptions;
}
